/* istanbul ignore file */
import { Suspense, useEffect, useState } from 'react';
import { Canvas } from '@react-three/fiber';
import { Bounds, Center, Environment, OrthographicCamera, TrackballControls, useProgress } from '@react-three/drei';
import { Icon, IconColor, IconID } from '@hub/common/Icon/Icon';
import { ModelGrid } from '@hub/common/ModelViewer/Grid/ModelGrid';
import { StlModel } from '@hub/common/ModelViewer/ModelContainers/StlModel';
import { GlbModel } from '@hub/common/ModelViewer/ModelContainers/glbModel';
import { Loader } from '@hub/common/ModelViewer/ModelLoader';
import { ThumbnailGenerator } from '@hub/common/ModelViewer/ThumbnailGenerator';

export const supportedFileFormat = {
  STL: 'stl',
  GLB: 'glb',
} as const;

type FileType = (typeof supportedFileFormat)[keyof typeof supportedFileFormat];

export type ModelViewerConfiguration = {
  width: number;
  height: number;
  materialColor: string;
  modelUrl?: string;
  fileType: FileType;
};

type FreeFormModelViewerProps = {
  configuration: ModelViewerConfiguration;
  onModelThumbnailGenerate?: (thumbnail: string) => void;
  children?: string | JSX.Element;
};

export type ModelProps = {
  modelUrl: string;
  materialColor: string;
  setModelSize: (size: { x: number; y: number; z: number }) => void;
};

export const ModelViewer = ({ configuration, onModelThumbnailGenerate, children }: FreeFormModelViewerProps) => {
  const { loaded } = useProgress(state => state);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [modelSize, setModelSize] = useState({ x: 0, y: 0, z: 0 });

  //  The model viewer does not render properly when the full screen icon is clicked.
  //  It causes a strange animation behaviour. This is a workaround to fix the issue.
  const transitionHelper = () => {
    if (isFullScreen) {
      const modelViewerWrapper = window.document.querySelector('[data-testid="model-viewer-wrapper"]');
      modelViewerWrapper.classList.add('opacity-0');
      setTimeout(() => {
        if (modelViewerWrapper) {
          modelViewerWrapper.classList.remove('opacity-0');
        }
      }, 100); // wait for full screen transition
    }
  };

  useEffect(() => {
    transitionHelper();
    if (!isFullScreen) {
      setShowGrid(false);
    }
  }, [isFullScreen]);

  return (
    <div
      data-testid="model-viewer-wrapper"
      className={`flex justify-center items-center w-full bg-gray-50 cursor-move ${
        isFullScreen ? 'fixed top-0 left-0 bottom-0 right-0 z-50' : 'relative'
      }`}
      style={
        !isFullScreen
          ? {
              width: '100%',
              maxWidth: configuration.width,
              height: 'auto',
              aspectRatio: `${configuration.width} / ${configuration.height}`,
              overflow: 'hidden',
            }
          : undefined
      }
    >
      <div className="absolute inset-0">
        {isFullScreen ? (
          <>
            <div
              data-testid="model-close-icon"
              onClick={() => setIsFullScreen(false)}
              className="absolute cursor-pointer z-[100] top-[30px] right-[50px]"
            >
              <Icon size={10} id={IconID.CLOSE_CROSS} primary={false} />
            </div>
            <div
              data-testid="model-show-grid-icon"
              onClick={() => setShowGrid(!showGrid)}
              className="absolute cursor-pointer z-[100] top-[26px] right-[100px]"
            >
              <Icon
                size={14}
                id={IconID.GRID_3D}
                color={showGrid ? IconColor.ORANGE : IconColor.DARK_GRAY}
                primary={false}
              />
            </div>
          </>
        ) : (
          <div
            data-testid="model-fullscreen-icon"
            className="absolute top-[10px] right-[10px] cursor-pointer z-[1]"
            onClick={() => setIsFullScreen(true)}
          >
            <Icon size={10} id={IconID.FULL_SCREEN_OPEN} primary={false} />
          </div>
        )}
        {children}
        {configuration.modelUrl ? (
          <Suspense fallback={<Loader />}>
            <Canvas
              resize={{ offsetSize: true }}
              gl={{ preserveDrawingBuffer: true }}
              camera={{ position: [50, 50, 50] }}
            >
              <OrthographicCamera>
                <Bounds margin={1.7}>
                  <Center>
                    {configuration.fileType === supportedFileFormat.GLB ? (
                      <GlbModel
                        modelUrl={configuration.modelUrl}
                        materialColor={configuration.materialColor}
                        setModelSize={setModelSize}
                      />
                    ) : (
                      <StlModel
                        modelUrl={configuration.modelUrl}
                        materialColor={configuration.materialColor}
                        setModelSize={setModelSize}
                      />
                    )}
                  </Center>
                </Bounds>
              </OrthographicCamera>
              {showGrid && <ModelGrid modelSize={modelSize} />}
              {loaded && <TrackballControls rotateSpeed={15} />}
              <Environment preset="apartment" />
              {loaded && onModelThumbnailGenerate && (
                <ThumbnailGenerator onModelThumbnailGenerate={onModelThumbnailGenerate} />
              )}
            </Canvas>
          </Suspense>
        ) : (
          <Loader />
        )}
      </div>
    </div>
  );
};
